import React, { useState, useEffect } from 'react';
import { Search, Plus, Star } from 'lucide-react';
import { menuApi } from '../services/api';
import { Helmet } from "react-helmet-async";

const Menu = ({ onAddToCart }) => {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, itemsRes] = await Promise.all([
          menuApi.getCategories(),
          menuApi.getItems()
        ]);
        setCategories(categoriesRes);
        setMenuItems(itemsRes.filter(item => item.is_available === true));
      } catch (error) {
        console.error('Error fetching menu data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category_id === activeCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <>
  <Helmet>
    <title>Meniul Zilei Vatra Dornei | Mâncare Gătită & Delivery | PanAghia</title>
    <meta
      name="description"
      content="Vezi meniul PanAghia din Vatra Dornei: meniul zilei, ciorbe, feluri principale, garnituri, mâncare gătită și livrare la domiciliu."
    />
    <link rel="canonical" href="https://www.panaghiaautoservirevatradornei.ro/meniu" />
  </Helmet>
      <div className="min-h-screen bg-gray-50 pt-24 flex items-center justify-center" data-testid="menu-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4A847]"></div>
      </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24" data-testid="menu-page">
      {/* Hero Section */}
      <section className="bg-[#D4A847] py-16" data-testid="menu-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Meniul Nostru
          </h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Descoperă preparatele noastre tradiționale, gătite zilnic cu ingrediente proaspete
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Caută în meniu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:border-[#D4A847] focus:ring-2 focus:ring-[#D4A847]/20 transition-all"
              data-testid="menu-search-input"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="menu-categories">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-5 py-2 rounded-full font-medium transition-all ${
              activeCategory === 'all'
                ? 'bg-[#D4A847] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
            data-testid="category-all-btn"
          >
            Toate
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-5 py-2 rounded-full font-medium transition-all ${
                activeCategory === category.id
                  ? 'bg-[#D4A847] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
              data-testid={`category-${category.id}-btn`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="menu-items-grid">
          {filteredItems.map((item) => (
            <div 
              key={item.id} 
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 group"
              data-testid={`menu-item-${item.id}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {item.is_popular && (
                  <div className="absolute top-3 left-3 bg-[#D4A847] text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" />
                    Popular
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xl font-bold text-[#D4A847]">{item.price} lei</span>
                  <button 
                    onClick={() => onAddToCart && onAddToCart(item)}
                    className="w-10 h-10 bg-[#D4A847] text-white rounded-full flex items-center justify-center hover:bg-[#c49a3d] transition-colors"
                    data-testid={`add-to-cart-${item.id}`}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-16" data-testid="no-items-message">
            <p className="text-gray-500 text-lg">Nu am găsit preparate care să corespundă căutării tale.</p>
            <button
              onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
              className="mt-4 text-[#D4A847] hover:underline"
              data-testid="reset-filters-btn"
            >
              Resetează filtrele
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Menu;
